import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Plus, Clock, Users, BadgeCheck, Star, CalendarDays, ExternalLink, Search, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FORMATS = [
  { id: "online", name: "Online ao vivo" },
  { id: "gravado", name: "Gravado" },
  { id: "presencial", name: "Presencial" },
];
const fmtName = (id) => FORMATS.find((f) => f.id === id)?.name || id;

export default function Mentor() {
  const [catalog, setCatalog] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [mine, setMine] = useState([]);
  const [canMentor, setCanMentor] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [specFilter, setSpecFilter] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", specialty: "outros", format: "online", price: "", duration_min: 60, date: "", max_seats: 20, link: "" });

  const load = () => {
    api.get("/mentor/enrollments/mine").then((r) => setEnrollments(r.data)).catch(() => {});
    api.get("/mentor/can-mentor").then((r) => setCanMentor(!!r.data.allowed)).catch(() => {});
    api.get("/assist/specialties").then((r) => setSpecialties(r.data.specialties)).catch(() => {});
    api.get("/mentor/content/mine").then((r) => setMine(r.data)).catch(() => {});
  };
  const loadCatalog = () => {
    api.get("/mentor/catalog", { params: { specialty: specFilter, q: qDebounced } }).then((r) => setCatalog(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 400); return () => clearTimeout(t); }, [q]);
  useEffect(() => { loadCatalog(); }, [specFilter, qDebounced]);

  const specName = (id) => specialties.find((s) => s.id === id)?.name || id;

  const enroll = async (c) => {
    try {
      const { data } = await api.post(`/mentor/content/${c.id}/enroll`);
      toast.success(data.message);
      load();
      loadCatalog();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const unenroll = async (contentId) => {
    try {
      await api.delete(`/mentor/content/${contentId}/enroll`);
      toast.success("Inscrição cancelada.");
      load();
      loadCatalog();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const publish = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/mentor/content", {
        ...form, price: Number(form.price) || 0,
        duration_min: Number(form.duration_min) || 60, max_seats: Number(form.max_seats) || 20,
      });
      toast.success(data.message);
      setForm({ title: "", description: "", specialty: "outros", format: "online", price: "", duration_min: 60, date: "", max_seats: 20, link: "" });
      load();
      loadCatalog();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const cancelContent = async (c) => {
    if (!window.confirm(`Cancelar a mentoria "${c.title}"? Os inscritos serão desligados.`)) return;
    try {
      await api.put(`/mentor/content/${c.id}/cancel`);
      toast.success("Mentoria cancelada.");
      load();
      loadCatalog();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const vagas = (c) => `${c.max_seats - c.seats_taken} de ${c.max_seats} vagas`;

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="mentor-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <GraduationCap className="h-6 w-6 text-sky-600" /> GEPR Mentor <Badge className="bg-amber-100 text-amber-700 border-amber-200">BETA</Badge>
        </h1>
        <p className="text-sm text-slate-500">Mentorias e treinamentos de especialistas verificados para acelerar sua carreira</p>
      </div>

      <Tabs defaultValue="catalogo">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="catalogo" data-testid="mentor-tab-catalog">Catálogo</TabsTrigger>
          <TabsTrigger value="inscricoes" data-testid="mentor-tab-enrollments">Minhas inscrições ({enrollments.length})</TabsTrigger>
          <TabsTrigger value="publicar" data-testid="mentor-tab-publish">Publicar mentoria</TabsTrigger>
        </TabsList>

        <TabsContent value="catalogo" className="mt-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-3.5 text-slate-400" />
              <Input className="h-12 pl-9" placeholder="Buscar mentoria..." data-testid="mentor-search" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={specFilter} onValueChange={setSpecFilter}>
              <SelectTrigger className="h-12 w-44" data-testid="mentor-spec-filter"><SelectValue placeholder="Especialidade" /></SelectTrigger>
              <SelectContent>
                {specialties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {catalog.length === 0 && (
            <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="no-mentor-content">
              Nenhuma mentoria publicada no momento. Especialistas verificados podem publicar na aba <b>Publicar mentoria</b>.
            </CardContent></Card>
          )}
          {catalog.map((c) => (
            <Card key={c.id} className="border-slate-200" data-testid={`mc-card-${c.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{c.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 flex-wrap">
                      {c.mentor_name} {c.mentor_verified && <BadgeCheck className="h-3.5 w-3.5 text-sky-600" />}
                      {c.rating_count > 0 && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400 fill-amber-400" />{c.rating_avg.toFixed(1)}</span>}
                      · {specName(c.specialty)}
                    </p>
                  </div>
                  <p className="font-mono-num font-semibold text-sky-700 shrink-0">{c.price > 0 ? brl(c.price) : "Gratuita"}</p>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2">{c.description}</p>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">{fmtName(c.format)}</Badge>
                    {c.date && <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {c.date}</span>}
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {c.duration_min} min</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {vagas(c)}</span>
                  </div>
                  {c.is_mine ? (
                    <Badge variant="outline" className="text-[10px]">Você é o mentor</Badge>
                  ) : c.enrolled ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px]" data-testid={`mc-enrolled-${c.id}`}>Inscrito</Badge>
                  ) : c.seats_taken >= c.max_seats ? (
                    <Badge variant="outline" className="text-[10px] text-slate-500">Lotada</Badge>
                  ) : (
                    <Button size="sm" className="h-10 bg-sky-600 hover:bg-sky-700 text-white" onClick={() => enroll(c)} data-testid={`mc-enroll-${c.id}`}>Inscrever-se</Button>
                  )}
                </div>
                {c.price > 0 && !c.is_mine && <p className="text-[11px] text-slate-400">Pagamento combinado diretamente com o mentor nesta fase beta.</p>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="inscricoes" className="mt-4 space-y-2">
          {enrollments.length === 0 && <p className="text-sm text-slate-500">Você ainda não se inscreveu em nenhuma mentoria.</p>}
          {enrollments.map((e) => (
            <Card key={e.id} className="border-slate-200" data-testid={`me-card-${e.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">{e.title}</p>
                    <p className="text-xs text-slate-500">com {e.mentor_name} · {fmtName(e.format)}{e.date ? ` · ${e.date}` : ""} · {e.duration_min} min {e.content_status !== "publicado" && "· cancelada pelo mentor"}</p>
                  </div>
                  <p className="font-mono-num text-sm text-sky-700 shrink-0">{e.price > 0 ? brl(e.price) : "Gratuita"}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {e.link && e.content_status === "publicado" && (
                    <a href={e.link} target="_blank" rel="noreferrer" data-testid={`me-link-${e.id}`}>
                      <Button size="sm" className="h-10 bg-sky-600 hover:bg-sky-700 text-white">
                        <ExternalLink className="h-4 w-4 mr-1" /> Acessar conteúdo
                      </Button>
                    </a>
                  )}
                  <Button size="sm" variant="ghost" className="h-10 text-red-600 hover:bg-red-50" onClick={() => unenroll(e.content_id)} data-testid={`me-cancel-${e.id}`}>Cancelar inscrição</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="publicar" className="mt-4 space-y-4">
          {!canMentor ? (
            <Card className="border-amber-200 bg-amber-50"><CardContent className="p-6 text-sm text-amber-800" data-testid="mentor-not-allowed">
              A publicação de mentorias é exclusiva para <b>especialistas verificados</b> pela equipe GEPR. Crie seu perfil no GEPR Assist, acumule atendimentos bem avaliados e solicite a verificação.
            </CardContent></Card>
          ) : (
            <Card className="border-sky-300 bg-sky-50">
              <CardHeader><CardTitle className="text-base">Publicar nova mentoria</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={publish} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>Título *</Label>
                    <Input className="h-12 bg-white" data-testid="mp-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Diagnóstico de falhas em placas inverter na prática" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Descrição * (o que o aluno vai aprender)</Label>
                    <Textarea rows={3} className="bg-white" data-testid="mp-description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>Especialidade</Label>
                      <Select value={form.specialty} onValueChange={(v) => setForm({ ...form, specialty: v })}>
                        <SelectTrigger className="h-12 bg-white" data-testid="mp-specialty"><SelectValue /></SelectTrigger>
                        <SelectContent>{specialties.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Formato</Label>
                      <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                        <SelectTrigger className="h-12 bg-white" data-testid="mp-format"><SelectValue /></SelectTrigger>
                        <SelectContent>{FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Preço (R$)</Label>
                      <Input className="h-12 bg-white font-mono-num" type="number" step="0.01" min="0" data-testid="mp-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0 = gratuita" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Duração (min)</Label>
                      <Input className="h-12 bg-white font-mono-num" type="number" min="15" step="5" data-testid="mp-duration" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Data</Label>
                      <Input className="h-12 bg-white" type="date" data-testid="mp-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Vagas</Label>
                      <Input className="h-12 bg-white font-mono-num" type="number" min="1" max="100" data-testid="mp-seats" value={form.max_seats} onChange={(e) => setForm({ ...form, max_seats: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Link de acesso (Meet/YouTube/etc — visível só para inscritos)</Label>
                    <Input className="h-12 bg-white" data-testid="mp-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." />
                  </div>
                  <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" disabled={sending} data-testid="mp-submit">
                    <Plus className="h-4 w-4 mr-2" /> {sending ? "Publicando..." : "Publicar mentoria"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {canMentor && mine.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Minhas mentorias publicadas</p>
              {mine.map((c) => (
                <Card key={c.id} className="border-slate-200" data-testid={`mine-card-${c.id}`}>
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{c.title} {c.status !== "publicado" && <Badge variant="outline" className="ml-1 text-[10px]">{c.status}</Badge>}</p>
                      <p className="text-xs text-slate-500">{fmtName(c.format)}{c.date ? ` · ${c.date}` : ""} · {c.seats_taken}/{c.max_seats} inscritos · {c.price > 0 ? brl(c.price) : "gratuita"}</p>
                    </div>
                    {c.status === "publicado" && (
                      <Button size="sm" variant="ghost" className="h-10 text-red-600 hover:bg-red-50" onClick={() => cancelContent(c)} data-testid={`mcancel-${c.id}`}>Cancelar</Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
